/*
 * DATA_RIGHTS: HONEYWELL CONFIDENTIAL AND PROPRIETARY
 * THIS WORK CONTAINS VALUABLE CONFIDENTIAL AND PROPRIETARY
 * INFORMATION. DISCLOSURE, USE OR REPRODUCTION OUTSIDE OF
 * HONEYWELL INTERNATIONAL, INC. IS PROHIBITED EXCEPT AS
 * AUTHORIZED IN WRITING. THIS UNPUBLISHED WORK IS PROTECTED BY
 * THE LAWS OF THE UNITED STATES AND OTHER COUNTRIES. IN THE
 * EVENT OF PUBLICATION, THE FOLLOWING NOTICE SHALL APPLY:
 * Copyright (C) 2022 HONEYWELL INTERNATIONAL, INC., ALL RIGHTS RESERVED.
 *
 * Created by H144765 on 3/31/2022.
 */

import {apiUrl, getEnumParams, handleApiCall, securedApiOptions} from './HttpUtils'

const getAssetFaultsApiUrl = (assetId, faultId) => {
    return apiUrl(`assets/${assetId}/faults/${faultId}`)
}

const getFaultsApiUrl = () => {
    return apiUrl(`faults`)
}

const getAssetApiUrl = (assetId) => {
    return apiUrl(`assets/${assetId}`)
}

/**
 * Compose query filter for fetching fault condition list
 * @param states
 * @param search
 * @param sort
 * @param ids
 * @returns {string}
 */
export const composeFaultQueryWithoutPagination = (states, search, sort, ids, assigneeId) => {
    const filterObj = {
        search: search,
        sort: sort,
    }
    return composeFaultQuery(states, filterObj, ids, assigneeId);
}

/**
 * Compose query filter for fetching fault condition list with pagination parameters
 * @param states
 * @param filterObj contains search, start, limit, sort values
 * @param ids
 * @returns {string}
 */
export const composeFaultQuery = (states, filterObj, ids, assigneeId) => {
    let query = '?start=' + encodeURIComponent(filterObj.start ? filterObj.start : "")
    query += '&limit=' + encodeURIComponent(filterObj.limit ? filterObj.limit : "")
    query += getEnumParams('states', states)
    if (filterObj.search && filterObj.search.length > 0) {
        query += '&search=' + encodeURIComponent(filterObj.search)
    }
    if (filterObj.sort && filterObj.sort.length > 0) {
        query += '&sort=' + encodeURIComponent(filterObj.sort)
    }
    if (assigneeId !== undefined && assigneeId != null) {
        query += '&assigneeId=' + assigneeId
    }
    query += getEnumParams('ids', ids)
    return query
}

export const fetchObservations = (assetId, faultId, types = [], states = []) => {
    let query = '?start=0';
    query += getEnumParams('symptomTypes', types)
    query += getEnumParams('states', states)
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + '/observations' + query)
}

export const submitObservations = (assetId, faultId, observations) => {
    const opts = securedApiOptions()
    opts.method = 'PUT'
    opts.body = JSON.stringify(observations)
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + '/observations', opts)
}

export const fetchActions = (assetId, faultId) => {
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + '/actions')
}

export const submitTestResult = (assetId, faultId, testId, testValue, learningLoopTest, learningLoopTestResult) => {
    const opts = securedApiOptions()
    opts.method = 'PUT'
    opts.body = JSON.stringify(testValue)
    const query = `?learningLoopTest=${learningLoopTest}&learningLoopTestResult=${learningLoopTestResult}`
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + `/tests/${testId}` + query, opts)
}

export const fetchTestResultsForTest = (assetId, faultId, testId) => {
    const opts = securedApiOptions()
    opts.method = 'GET'
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + "/tests/" + testId, opts)
}

export const createNewTestResult = (assetId, testId, testResultName, learningLoopTest) => {
    const opts = securedApiOptions()
    opts.method = 'PUT'
    const query = '?testId=' + testId + '&testResultName=' + testResultName + '&learningLoopTest=' + learningLoopTest
    return handleApiCall(getAssetApiUrl(assetId) + "/createNewTestResult" + query, opts)
}

export const createNewTest = (assetId, faultId, testName) => {
    const opts = securedApiOptions()
    opts.method = 'PUT'
    const query = '?testName=' + testName
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + "/createNewTest/" + query, opts)
}

export const deleteTestResult = (assetId, faultId, testResultId) => {
    const opts = securedApiOptions()
    opts.method = 'DELETE'
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + '/deleteTestResult/' + testResultId, opts)
}

export const deleteTest = (assetId, faultId, testId) => {
    const opts = securedApiOptions()
    opts.method = 'DELETE'
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + '/deleteTest/' + testId, opts)
}

export const fetchActionDetail = (assetId, actionId, faultId, actionType) => {
    let query = '?type=' + actionType
    if (faultId) {
        query += '&fault=' + faultId
    }
    return handleApiCall(getAssetApiUrl(assetId) + `/action/${actionId}` + query)
}

export const submitCorrectiveActionResult = (assetId, faultId, actionId, actionValue) => {
    const opts = securedApiOptions()
    opts.method = 'PUT'
    opts.body = actionValue.toString()
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + `/actions/${actionId}`, opts)
}

export const saveComment = (assetId, faultId, comment) => {
    const opts = securedApiOptions()
    opts.method = 'POST'
    opts.body = comment
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + `/comments`, opts)
}

export const updateComment = (assetId, faultId, commentId, comment) => {
    const opts = securedApiOptions()
    opts.method = 'PUT'
    opts.body = comment
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + `/comments/${commentId}`, opts)
}

export const fetchActionLog = (assetId, faultId, primaryFaultId, actionTypes, objectTypes) => {
    let query = '?start=0';
    if (faultId) {
        query += '&faultId=' + faultId
    }
    if (primaryFaultId) {
        query += '&primaryFaultId=' + primaryFaultId
    }
    query += getEnumParams('actionTypes', actionTypes)
    query += getEnumParams('objectTypes', objectTypes)
    return handleApiCall(getAssetApiUrl(assetId) + '/log' + query)
}

/**
 * Fetch all faults for given asset based on filter conditions
 * @param assetId
 * @param states
 * @param filterObj contains search, start, limit, sort values
 * @param ids
 * @returns {*}
 */
export const fetchAssetFaults = (assetId, states, filterObj, ids) => {
    return handleApiCall(getAssetApiUrl(assetId) + `/faults` + composeFaultQuery(states, filterObj, ids, ""))
}

/**
 * Fetch all faults which can current user sees based on filter conditions
 * @param states
 * @param search
 * @param sort
 * @param ids
 * @returns {*}
 */
export const fetchFaultsWithoutPagination = (states, search, sort, ids, assignee) => {
    const filterObj = {
        search: search,
        sort: sort,
    }
    return fetchFaults(states, filterObj, ids, assignee)
}

/**
 * Fetch all faults with pagination parameters
 * @param states
 * @param filterObj contains search, start, limit, sort values
 * @param ids
 * @returns {*}
 */
export const fetchFaults = (states, filterObj, ids, assignee) => {
    return handleApiCall(getFaultsApiUrl() + composeFaultQuery(states, filterObj, ids, assignee))
}

export const fetchComments = (assetId, faultId) => {
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + '/comments')
}

export const fetchDetails = (assetId, faultId) => {
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId))
}

export const submitConfigSymptom = (assetId, faultId, id, value) => {
    const symptoms = [{id: id, state: value, description: ''}]
    const opts = securedApiOptions()
    opts.method = 'PUT'
    opts.body = JSON.stringify(symptoms)
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + '/configurationSymptoms', opts)
}

export const setFaultState = (assetId, faultId, state, observations, comment = null) => {
    const opts = securedApiOptions()
    opts.method = 'PUT'
    opts.body = JSON.stringify({
        state: state,
        observations: observations,
        comment: comment,
    })
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + '/state', opts)
}

export const fetchTechPubDocument = (assetId, techPubId) => {
    return handleApiCall(getAssetApiUrl(assetId) + `/techpub/${techPubId}`)
}


export const assignUser = (assetId, faultId, userId) => {
    const opts = securedApiOptions()
    opts.method = 'PUT'
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + `/assignee/${userId}`, opts)
}

export const assignToSelf = (assetId, faultId) => {
    const opts = securedApiOptions()
    opts.method = 'PUT'
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + `/assignee/self/`, opts)
}

export const fetchDwp = (assetId, faultId) => {
    const opts = securedApiOptions()
    opts.method = 'GET'
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + '/dwp', opts)
}

export const fetchAllNamesExceptAllocated = (assetId, faultId, type) => {
    const opts = securedApiOptions()
    opts.method = 'GET'
    const query = '?type=' + type
    return handleApiCall(getAssetFaultsApiUrl(assetId, faultId) + "/otherNames" + query, opts)
}

export const autoCloseInactiveFaults = () => {
    const opts = securedApiOptions()
    opts.method = 'GET'
    return handleApiCall(getFaultsApiUrl() + "/autoclose", opts)
}

export const fetchCloseComments = () => {
    const opts = securedApiOptions();
    opts.method = 'GET';
    return handleApiCall(apiUrl(`uiConfig`) + "/closeComments", opts)
}